import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { probabilitiesSelectors, probabilitiesThunks } from '@ucheba/store/probabilities'
import { ELoading } from '@ucheba/store/utils/response/types'
import { profileSelectors, profileThunks } from '@ucheba/store/profile'
import {
  personalAchievementsSelectors,
  personalAchievementsThunks,
} from '@ucheba/store/personalAchievements'

interface IUseSelectDialogProps {
  programId: number
  additionalSubjectsLength?: number
  onClick: () => void
}

interface IInitialValues {
  achievements: number[]
  requiredExams: {
    examId: number
    name: string
    score: number | string | null
  }[]
  additionalExams: string[] | null
  additionalExamsScore: number | null
}

interface IUseSelectDialog {
  (props: IUseSelectDialogProps): {
    onSubmit: (data: IInitialValues) => void
    error: boolean
    isLoading: boolean
  }
}

const getExams = (
  exams: IInitialValues['requiredExams'],
  additionalExams: string[] | null,
  addExamScore: number | null
): {
  examId: number
  score: null | number
}[] => {
  const result = exams.map((exam) => {
    return {
      examId: exam.examId,
      score: exam.score || typeof exam.score === 'number' ? Number(exam.score) : null,
    }
  })

  if (additionalExams) {
    const addExams = {
      examId: Number(additionalExams[0]),
      score: Number(addExamScore),
    }

    result.push(addExams)
  }

  return result
}

export const useSelectDialog: IUseSelectDialog = (props) => {
  const { programId, additionalSubjectsLength, onClick } = props
  const dispatch = useDispatch()
  const [error, setError] = useState(false)
  const loading = useSelector(probabilitiesSelectors.loading)
  const loadingAchievement = useSelector(personalAchievementsSelectors.loading)
  const profile = useSelector(profileSelectors.loading)

  const isLoading = useMemo(() => {
    return (
      loading === ELoading.pending ||
      loadingAchievement === ELoading.pending ||
      profile === ELoading.pending
    )
  }, [loading, loadingAchievement, profile])

  const onSubmit = useCallback(
    async (data: IInitialValues) => {
      if (error) {
        setError(false)
      }

      if (typeof additionalSubjectsLength === 'number' && additionalSubjectsLength > 0) {
        if (
          data.additionalExamsScore &&
          data.additionalExams &&
          data.additionalExams?.length > 0 &&
          data.requiredExams.every((exam) => {
            return exam.score
          })
        ) {
          await dispatch(
            probabilitiesThunks.post({
              data: {
                type: 'program',
                referId: programId,
                scores: getExams(
                  data.requiredExams,
                  data.additionalExams,
                  data.additionalExamsScore
                ),
                achievementIds: data.achievements,
                readyToExam: true,
              },
            })
          )

          await dispatch(
            profileThunks.patch({
              data: {
                enrolleeInfo: {
                  exams: getExams(
                    data.requiredExams,
                    data.additionalExams,
                    data.additionalExamsScore
                  ),
                  achievementIds: data.achievements,
                  readyForAdditionalExam: true,
                },
              },
            })
          )

          onClick()
          await dispatch(profileThunks.get({}))
        } else {
          setError(true)
        }
      } else if (
        data.requiredExams.every((exam) => {
          return exam.score
        })
      ) {
        await dispatch(
          probabilitiesThunks.post({
            data: {
              type: 'program',
              referId: programId,
              scores: getExams(
                data.requiredExams,
                data.additionalExams,
                data.additionalExamsScore
              ),
              achievementIds: data.achievements,
              readyToExam: true,
            },
          })
        )

        await dispatch(
          profileThunks.patch({
            data: {
              enrolleeInfo: {
                exams: getExams(
                  data.requiredExams,
                  data.additionalExams,
                  data.additionalExamsScore
                ),
                achievementIds: data.achievements,
                readyForAdditionalExam: true,
              },
            },
          })
        )
        onClick()
        await dispatch(profileThunks.get({}))
      } else {
        setError(true)
      }
    },
    [additionalSubjectsLength, programId, dispatch, error]
  )

  return {
    onSubmit,
    error,
    isLoading,
  }
}

interface IUseInitialValuesProps {
  requiredSubjects: {
    examId: number
    name: string
  }[]
  selectedExams?: {
    examId: number
    score: number | null
  }[]
  optionalExams?: {
    examId: number
    name: string
  }[]
  achievementsIds?: number[]
}

interface IUseInitialValues {
  (props: IUseInitialValuesProps): IInitialValues
}

export const useInitialValues: IUseInitialValues = (props) => {
  const { requiredSubjects, selectedExams, optionalExams, achievementsIds } = props

  /** Массив достижений */
  const achievements = useMemo(() => {
    if (achievementsIds) {
      return achievementsIds.map((id) => String(id))
    }

    return []
  }, [achievementsIds])

  /** Обязательные экзамены */
  const requiredExams = useMemo(() => {
    if (requiredSubjects) {
      return requiredSubjects.map((exam) => {
        const selectedExam = selectedExams?.find((el) => el.examId === exam.examId)

        return {
          examId: exam.examId,
          score: selectedExam ? selectedExam.score : null,
          name: exam?.name,
        }
      })
    }
    return []
  }, [requiredSubjects, selectedExams])

  /** Получаем выбранный экзамен с самым выоским добавленными баллами */
  const selectedOptionalExam = useMemo(() => {
    const selectedExamsInOptional = selectedExams?.filter((exam) => {
      return optionalExams?.some((el) => el.examId === exam.examId)
    })

    if (selectedExamsInOptional && optionalExams) {
      const result = selectedExamsInOptional.reduce(
        (acc, exam, key) => {
          if (key === 0 || !acc.score || (exam.score && exam.score > acc?.score)) {
            // eslint-disable-next-line no-param-reassign
            acc = exam
          }

          return acc
        },
        {} as {
          examId: number
          score: number | null
        }
      )

      return result
    }

    return null
  }, [selectedExams, optionalExams])

  /** Экзамены по выбору */
  const additionalExams = useMemo(() => {
    if (optionalExams && selectedOptionalExam) {
      const result = optionalExams.find((exam) => {
        return exam.examId === selectedOptionalExam.examId
      })

      if (result) {
        return [result.examId]
      }

      return null
    }

    return null
  }, [optionalExams, selectedOptionalExam])

  /** Получаем добавленные баллы из выбранного экзамена */
  const additionalExamsScore = useMemo(() => {
    if (
      additionalExams &&
      additionalExams.length &&
      selectedOptionalExam &&
      selectedOptionalExam.score
    ) {
      return selectedOptionalExam.score
    }

    return null
  }, [selectedOptionalExam, additionalExams])

  return {
    achievements,
    requiredExams,
    additionalExams,
    additionalExamsScore,
  }
}
